//#region imports
import React, { memo, useMemo, useCallback, useEffect } from 'react';

// virtualized list
import { Virtuoso } from 'react-virtuoso';

// constants
import { TimelineColumnWidth } from '../../timeline-constants';

// components
import FlightRow from './flight-row';
import RedLine from './red-line';
import GreyOverlay from '../../timeline-components/grey-overlay';
import AltPickupsSection from './alt-pickups-section';
import GateRelocationsSection from './gate-relocations-section';

// redux
import { useSelector } from 'react-redux';
import {
    selectFlightsByHour
} from '_redux/slices/timeline/timeline-flight-list';
import { selectHideCompletedFlights } from '_redux/slices/timeline/timeline-control';
import { selectCompletedFlightKeys } from '_redux/slices/timeline/timeline-bookings';

import { TimelineColumnWidthRaw } from '../../timeline-constants';

//#endregion

// styles outside component to prevent recreation
const containerStyle = {
    //minWidth: TimelineColumnWidthRaw,
    minWidth: TimelineColumnWidthRaw,
    maxWidth: TimelineColumnWidth,
    border: '2px solid lightgrey',
    padding: 0,
    margin: 0,
    //textAlign: 'center',
    height: '100%', // Fill parent height
    position: 'relative', // Enable absolute positioning for red line
};

// Add scrollbar styling
const scrollbarStyle = `
    .time-column-virtuoso::-webkit-scrollbar {
        width: 12px;
    }
    .time-column-virtuoso::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    .time-column-virtuoso::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 6px;
    }
    .time-column-virtuoso::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
`;

// Inject styles
if (typeof document !== 'undefined' && !document.getElementById('time-column-scrollbar-style')) {
    const style = document.createElement('style');
    style.id = 'time-column-scrollbar-style';
    style.textContent = scrollbarStyle;
    document.head.appendChild(style);
}

const TimeColumn = memo((props) => {

    // Memoize the selector to avoid creating a new function on every render
    const flightSelector = useMemo(() => selectFlightsByHour(props?.time), [props?.time]);
    const hourFlights = useSelector(flightSelector);

    // hide completed flights switch - drop rows whose PRM tasks are all done
    const hideCompletedFlights = useSelector(selectHideCompletedFlights);
    const completedFlightKeys = useSelector(selectCompletedFlightKeys);
    const flights = useMemo(() => {
        if (!hideCompletedFlights || completedFlightKeys.size === 0) return hourFlights;
        return hourFlights.filter((flight) => !completedFlightKeys.has(flight.bookingKey || flight.flightNumber || flight._id));
    }, [hourFlights, hideCompletedFlights, completedFlightKeys]);

    // Memoize item content renderer
    const itemContent = useCallback((index) => {
        const flight = flights[index];
        if (!flight) return null;
        return (
            <FlightRow
                flightKey={flight.bookingKey || flight.flightNumber || flight._id}
                handleGetBookings={props?.handleGetBookings}
            />
        );
    }, [flights]);

    // Memoize compute item key for better performance
    const computeItemKey = useCallback((index) => {
        const flight = flights[index];
        if (!flight) return `fallback-${index}`;
        return flight.bookingKey || flight.flightNumber || flight._id;
    }, [flights]);

    // header shown once at top of list (for ALT pickups and gate change tasks)
    const HeaderComponent = useCallback(() => {
        return (
            <>
                <GateRelocationsSection timeSlot={props?.time} />
                <AltPickupsSection timeSlot={props?.time} />
            </>
        )
    }, [props?.time]);

    // If no flights, return column with just ALT pickups / gate relocations (if any) and red line
    if (!flights || flights.length === 0) {
        return (
            <div style={{ ...containerStyle, height: props?.disableVirtuosoForIata ? 'auto' : '100%' }}>
                {props?.showRedLine && (
                    <RedLine index={props?.index} />
                )}
                {/* ALT pickups for this time slot */}
                <AltPickupsSection timeSlot={props?.time} />

                {/* Gate relocations for this time slot */}
                <GateRelocationsSection timeSlot={props?.time} />

            </div>
        );
    }

    return (
        <div style={{ ...containerStyle, height: props?.disableVirtuosoForIata ? 'auto' : '100%' }}>

            {/* red line */}
            {props?.showRedLine && (
                <RedLine index={props?.index} />
            )}

            <GreyOverlay show={props?.isInPast} opacity={0.05} />

            {/* Gate relocations for this time slot - shown at top of column */}
            {/* <GateRelocationsSection timeSlot={props?.time} /> */}

            {/* ALT pickups for this time slot - shown at top of column */}
            {/* <AltPickupsSection timeSlot={props?.time} /> */}


            {props?.disableVirtuosoForIata ? (
                <div style={{ width: '100%', opacity: props?.isInPast ? 0.5 : 1 }}>
                    <HeaderComponent />
                    {flights.map((flight) => (
                        <FlightRow
                            key={flight.bookingKey || flight.flightNumber || flight._id}
                            flightKey={flight.bookingKey || flight.flightNumber || flight._id}
                            handleGetBookings={props?.handleGetBookings}
                        />
                    ))}
                </div>
            ) : (
                <Virtuoso
                    className="time-column-virtuoso"
                    style={{
                        height: '100%',
                        width: '100%',
                        opacity: props?.isInPast ? 0.5 : 1
                    }}
                    totalCount={flights.length}
                    data={flights}
                    overscan={3}
                    components={{
                        Header: HeaderComponent,
                    }}
                    itemContent={itemContent}
                    computeItemKey={computeItemKey}
                />
            )}

        </div>
    );
}, (prevProps, nextProps) => {
    // re-render if time or showRedLine changes
    return prevProps.time === nextProps.time && prevProps.showRedLine === nextProps.showRedLine && prevProps.isInPast === nextProps.isInPast && prevProps.index === nextProps.index;
});

TimeColumn.displayName = 'TimeColumn';

export default TimeColumn;
